import {
  type PermissionDefinition,
  type PermissionType,
  type SingleOrOptionalEntityIds,
  EntityIdSchema,
  PERMISSION_DEFINITION,
  isNotEmpty,
} from '@terros/common'

export type PermissionDefinitionGroup = Map<PermissionType, PermissionDefinition>
export type GroupedPermissionDefinitions = Map<string, PermissionDefinitionGroup>

function buildPermissionGroups(): GroupedPermissionDefinitions {
  const grouped: GroupedPermissionDefinitions = new Map()
  Object.entries(PERMISSION_DEFINITION).forEach(([permissionType, definition]) => {
    const group = grouped.get(definition.category) ?? new Map()
    group.set(permissionType, definition)
    grouped.set(definition.category, group)
  })
  return grouped
}

const permissionGroups = Array.from(buildPermissionGroups().entries())

const buildPermissionsRecord = (): GroupedPermissionDefinitions => {
  return Array.from(permissionGroups)
    .sort(compareCategories)
    .reduce<GroupedPermissionDefinitions>((acc, [category, definitions]) => {
      const defs = toArray(definitions).sort(comparePermissions).filter(filterDeprecated)
      if (isNotEmpty(defs)) {
        acc.set(category, new Map(defs))
      }
      return acc
    }, new Map<string, PermissionDefinitionGroup>())
}

const comparePermissionTypes = (a: PermissionType, b: PermissionType): number => {
  const aTypeCompare = a.split(':')[0]
  const bTypeCompare = b.split(':')[0]
  if (aTypeCompare !== bTypeCompare) {
    return aTypeCompare.localeCompare(bTypeCompare)
  }
  return a.localeCompare(b)
}

type PermissionAndDef = [PermissionType, PermissionDefinition]
const comparePermissions = ([aType, aDef]: PermissionAndDef, [bType, bDef]: PermissionAndDef): number => {
  if (aType !== bType) {
    return comparePermissionTypes(aType, bType)
  }
  return aDef.label.localeCompare(bDef.label)
}

type PermissionCategory = [string, Map<PermissionType, PermissionDefinition>]
const compareCategories = (a: PermissionCategory, b: PermissionCategory): number => {
  const [aCategory] = a
  const [bCategory] = b
  return aCategory.localeCompare(bCategory)
}

const filterDeprecated = ([_type, def]: PermissionAndDef): boolean => !def.deprecated

const toArray = (definitions: PermissionDefinitionGroup): PermissionAndDef[] => Array.from(definitions.entries())

export const permissionsRecord = buildPermissionsRecord()

export const toValidPermissionIds = (
  ids: (string | undefined | null)[] | string | undefined | null
): SingleOrOptionalEntityIds | undefined => {
  const entityIdParse = EntityIdSchema.safeParse(ids)
  if (entityIdParse.success) return entityIdParse.data

  if (Array.isArray(ids)) {
    return ids.map((id) => EntityIdSchema.safeParse(id).data).filter(isNotEmpty)
  }
}
